import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  fetchNotificationsPage,
  fetchUnreadCount,
  hideAllNotifications,
  hideNotification,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationsPage,
} from '../api';
import type { NotificationDto, NotificationReadFilter } from '../api';
import { useNotificationsStore } from './notificationsStore';

export const notificationsKeys = {
  all: ['notifications'] as const,
  listPrefix: ['notifications', 'list'] as const,
  list: (filter: NotificationReadFilter) => ['notifications', 'list', filter] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
};

export function useNotificationsInfiniteQuery(filter: NotificationReadFilter) {
  return useInfiniteQuery({
    queryKey: notificationsKeys.list(filter),
    queryFn: ({ pageParam }) =>
      fetchNotificationsPage({ filter, cursor: pageParam ?? null, limit: 20 }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 15_000,
  });
}

export function useUnreadCountQuery() {
  const setUnreadCount = useNotificationsStore((s) => s.setUnreadCount);
  const query = useQuery({
    queryKey: notificationsKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (typeof query.data === 'number') setUnreadCount(query.data);
  }, [query.data, setUnreadCount]);

  return query;
}

type ListQueryData = { pages: NotificationsPage[]; pageParams: unknown[] };

function updateAllListCaches(
  qc: ReturnType<typeof useQueryClient>,
  updater: (item: NotificationDto) => NotificationDto | null,
): void {
  qc.setQueriesData<ListQueryData>(
    { queryKey: notificationsKeys.listPrefix, exact: false },
    (data) => {
      if (!data || !Array.isArray(data.pages)) return data;
      const nextPages = data.pages.map((page) => {
        const nextItems: NotificationDto[] = [];
        for (const item of page.items) {
          const updated = updater(item);
          if (updated !== null) nextItems.push(updated);
        }
        return { ...page, items: nextItems };
      });
      return { ...data, pages: nextPages };
    },
  );
}

export function useMarkNotificationReadMutation() {
  const qc = useQueryClient();
  const decrementUnread = useNotificationsStore((s) => s.decrementUnread);

  return useMutation<void, Error, string, { wasUnread: boolean }>({
    mutationFn: markNotificationRead,
    onMutate: (id) => {
      let wasUnread = false;
      updateAllListCaches(qc, (n) => {
        if (n.id !== id) return n;
        if (!n.isRead) wasUnread = true;
        return { ...n, isRead: true };
      });
      if (wasUnread) decrementUnread();
      return { wasUnread };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.wasUnread) {
        useNotificationsStore.getState().incrementUnread();
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationsKeys.listPrefix });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const qc = useQueryClient();

  return useMutation<number, Error, void, { previousUnread: number }>({
    mutationFn: markAllNotificationsRead,
    onMutate: () => {
      const previousUnread = useNotificationsStore.getState().unreadCount;
      updateAllListCaches(qc, (n) => (n.isRead ? n : { ...n, isRead: true }));
      useNotificationsStore.getState().setUnreadCount(0);
      return { previousUnread };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) useNotificationsStore.getState().setUnreadCount(ctx.previousUnread);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationsKeys.listPrefix });
    },
  });
}

export function useHideAllNotificationsMutation() {
  const qc = useQueryClient();

  return useMutation<number, Error, NotificationReadFilter, { previousUnread: number }>({
    mutationFn: hideAllNotifications,
    onMutate: (filter) => {
      const previousUnread = useNotificationsStore.getState().unreadCount;
      updateAllListCaches(qc, (n) => {
        const inTab = filter === 'all' || (filter === 'unread' ? !n.isRead : n.isRead);
        return inTab ? null : n;
      });
      if (filter !== 'read') useNotificationsStore.getState().setUnreadCount(0);
      return { previousUnread };
    },
    onError: (_e, _filter, ctx) => {
      if (ctx) useNotificationsStore.getState().setUnreadCount(ctx.previousUnread);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationsKeys.listPrefix });
    },
  });
}

export function useHideNotificationMutation() {
  const qc = useQueryClient();
  const decrementUnread = useNotificationsStore((s) => s.decrementUnread);

  return useMutation<void, Error, string, { wasUnread: boolean }>({
    mutationFn: hideNotification,
    onMutate: (id) => {
      let wasUnread = false;
      updateAllListCaches(qc, (n) => {
        if (n.id !== id) return n;
        if (!n.isRead) wasUnread = true;
        return null;
      });
      if (wasUnread) decrementUnread();
      return { wasUnread };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.wasUnread) {
        useNotificationsStore.getState().incrementUnread();
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
      void qc.invalidateQueries({ queryKey: notificationsKeys.listPrefix });
    },
  });
}

export function prependStreamedNotification(
  qc: ReturnType<typeof useQueryClient>,
  notification: NotificationDto,
): void {
  const targets: QueryKey[] = [
    notificationsKeys.list('all'),
    notification.isRead
      ? notificationsKeys.list('read')
      : notificationsKeys.list('unread'),
  ];

  for (const key of targets) {
    qc.setQueryData<ListQueryData>(key, (data) => {
      if (!data || data.pages.length === 0) return data;
      const [firstPage, ...rest] = data.pages;
      const alreadyPresent = firstPage.items.some((n) => n.id === notification.id);
      if (alreadyPresent) {
        return {
          ...data,
          pages: [
            {
              ...firstPage,
              items: firstPage.items.map((n) =>
                n.id === notification.id ? notification : n,
              ),
            },
            ...rest,
          ],
        };
      }
      return {
        ...data,
        pages: [
          { ...firstPage, items: [notification, ...firstPage.items] },
          ...rest,
        ],
      };
    });
  }
}

export type { NotificationsPage };
